import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { dancersApi } from '../services/api';
import { Dancer, UpdateDancerProfileData } from '../types';

const DancersManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingDancer, setEditingDancer] = useState<Dancer | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const queryClient = useQueryClient();

  const { data: dancersData, isLoading } = useQuery({
    queryKey: ['dancers', { search, statusFilter }],
    queryFn: () => dancersApi.getAll({
      search: search || undefined,
      active: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
    }),
  });

  const { register, handleSubmit, reset } = useForm<UpdateDancerProfileData>();

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateDancerProfileData }) =>
      dancersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dancers'] });
      reset();
      setEditingDancer(null);
      setShowForm(false);
    },
  });

  const activateMutation = useMutation({
    mutationFn: dancersApi.activate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dancers'] });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: dancersApi.deactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dancers'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: dancersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dancers'] });
    },
  });

  const onSubmit = (data: UpdateDancerProfileData) => {
    if (editingDancer) {
      updateMutation.mutate({ id: editingDancer.id, data });
    }
  };

  const handleEdit = (dancer: Dancer) => {
    setEditingDancer(dancer);
    reset({
      bio: dancer.bio || '',
      profileImage: dancer.profileImage || '',
    });
    setShowForm(true);
  };

  const handleDelete = (dancer: Dancer) => {
    if (window.confirm(`Are you sure you want to delete "${dancer.name}"? This will also delete all their demonstrations.`)) {
      deleteMutation.mutate(dancer.id);
    }
  };

  const handleActivate = (dancer: Dancer) => {
    activateMutation.mutate(dancer.id);
  };

  const handleDeactivate = (dancer: Dancer) => {
    if (window.confirm(`Deactivating "${dancer.name}" will revoke their access and prevent them from uploading new demonstrations. Continue?`)) {
      deactivateMutation.mutate(dancer.id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingDancer(null);
    reset();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="dancers-management">
      <div className="page-header">
        <h1>Dancers Management</h1>
      </div>

      {showForm && editingDancer && (
        <div className="form-card">
          <h2>Edit Dancer Profile</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editingDancer.email}
                  disabled
                  className="disabled"
                />
              </div>

              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={editingDancer.name}
                  disabled
                  className="disabled"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="bio">Bio</label>
              <textarea
                id="bio"
                {...register('bio')}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="profileImage">Profile Image URL</label>
              <input
                type="url"
                id="profileImage"
                {...register('profileImage')}
                placeholder="https://..."
              />
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={updateMutation.isPending}>
                Update Dancer
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search dancers..."
          aria-label="Search dancers"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
          aria-label="Filter by status"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {isLoading ? (
        <div className="loading">Loading dancers...</div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Dancer</th>
                <th>Email</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dancersData?.data.map((dancer) => (
                <tr key={dancer.id}>
                  <td>
                    <div className="dancer-cell">
                      {dancer.profileImage ? (
                        <img src={dancer.profileImage} alt={dancer.name} className="dancer-avatar" />
                      ) : (
                        <div className="dancer-avatar-placeholder">
                          {dancer.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <strong>{dancer.name}</strong>
                        {dancer.bio && <small className="dancer-bio">{dancer.bio.substring(0, 50)}...</small>}
                      </div>
                    </div>
                  </td>
                  <td>{dancer.email}</td>
                  <td>
                    <span className={`status-badge ${dancer.isActive ? 'active' : 'inactive'}`}>
                      {dancer.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{formatDate(dancer.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      {dancer.isActive ? (
                        <button
                          className="btn btn-small btn-warning"
                          onClick={() => handleDeactivate(dancer)}
                          title="Deactivate"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          className="btn btn-small btn-success"
                          onClick={() => handleActivate(dancer)}
                          title="Activate"
                        >
                          Activate
                        </button>
                      )}
                      <button
                        className="btn btn-small btn-secondary"
                        onClick={() => handleEdit(dancer)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => handleDelete(dancer)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {dancersData?.data.length === 0 && (
                <tr>
                  <td colSpan={5} className="empty-state">
                    No dancers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DancersManagement;
